import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Send } from "lucide-react";
import { api, CATEGORIES, DISCIPLINES } from "../lib/api";
import { Header } from "../components/Header";

const FOCUS_OPTS = ["Women", "LGBTQ+", "BIPOC"];

export default function Submit() {
  const [form, setForm] = useState({
    title: "", organization: "", category: "Open Call", discipline: "Interdisciplinary",
    focus_tags: ["Women"], location: "", deadline: "", amount: "", url: "", description: "",
    submitter_name: "", submitter_email: "",
  });
  const [state, setState] = useState("idle"); // idle | loading | success
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    set("focus_tags", form.focus_tags.includes(t)
      ? form.focus_tags.filter((x) => x !== t)
      : [...form.focus_tags, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.organization) { setError("Title and organization are required."); return; }
    setState("loading"); setError("");
    try {
      await api.post("/submit", form);
      setState("success");
      window.scrollTo(0, 0);
    } catch {
      setError("Something went wrong. Please try again.");
      setState("idle");
    }
  };

  const field = "w-full brutal-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink";

  if (state === "success") {
    return (
      <div className="App bg-white">
        <Header />
        <div className="min-h-[70vh] flex items-center justify-center p-4">
          <div data-testid="submit-success" className="bg-white brutal-border brutal-shadow max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-pink border-2 border-black mb-5"><Check size={28} /></div>
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl mb-2">Thank you!</h2>
            <p className="text-black/70 mb-6">Your opportunity has been submitted for review. Once approved, it'll appear in the directory. 🖤</p>
            <Link to="/" data-testid="submit-back-home" className="inline-flex items-center justify-center gap-2 w-full label-caps bg-black text-white py-3 hover:bg-pink hover:text-black transition-colors">
              Back to the directory
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App bg-white">
      <Header />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/" data-testid="submit-back-link" className="label-caps inline-flex items-center gap-2 text-black/60 hover:text-pink mb-6">
          <ArrowLeft size={16} /> Back
        </Link>
        <p className="label-caps text-pink mb-3">Community Submission</p>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mb-3">Submit an opportunity</h1>
        <p className="text-black/70 mb-8 max-w-2xl">
          Running an open call, grant, residency, or job for artists? Share it below. Submissions are reviewed before appearing in the directory.
        </p>

        <form onSubmit={submit} className="brutal-border brutal-shadow bg-white p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label-caps">Title *</label>
            <input data-testid="submit-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Organization *</label>
            <input data-testid="submit-org" className={field} value={form.organization} onChange={(e) => set("organization", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Link (Apply URL)</label>
            <input data-testid="submit-url" className={field} value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://" />
          </div>
          <div>
            <label className="label-caps">Category</label>
            <select data-testid="submit-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Discipline</label>
            <select data-testid="submit-discipline" className={field} value={form.discipline} onChange={(e) => set("discipline", e.target.value)}>
              {DISCIPLINES.filter((d) => d !== "All").map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Location</label>
            <input data-testid="submit-location" className={field} value={form.location} onChange={(e) => set("location", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Deadline</label>
            <input data-testid="submit-deadline" className={field} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} placeholder="e.g. Sept 15, 2026 or Rolling" />
          </div>
          <div>
            <label className="label-caps">Amount / Award</label>
            <input data-testid="submit-amount" className={field} value={form.amount} onChange={(e) => set("amount", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-caps">Focus</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FOCUS_OPTS.map((t) => (
                <button key={t} type="button" data-testid={`submit-tag-${t}`} onClick={() => toggleTag(t)}
                  className={`label-caps px-3 py-1.5 border-2 border-black ${form.focus_tags.includes(t) ? "bg-pink text-black" : "bg-white"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="label-caps">Description</label>
            <textarea data-testid="submit-description" rows={4} className={field} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Your name (optional)</label>
            <input data-testid="submit-name" className={field} value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} />
          </div>
          <div>
            <label className="label-caps">Your email (optional)</label>
            <input data-testid="submit-email" type="email" className={field} value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} />
          </div>

          {error && <p data-testid="submit-error" className="sm:col-span-2 text-sm text-red-600">{error}</p>}
          <div className="sm:col-span-2">
            <button data-testid="submit-btn" type="submit" disabled={state === "loading"}
              className="inline-flex items-center justify-center gap-2 label-caps bg-black text-white px-8 py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50">
              <Send size={16} /> {state === "loading" ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
